import { trackEvent } from "@finn-no/pulse-sdk";
import IconLandscape48 from "@warp-ds/icons/react/landscape-48";
import { Badge, Card, Clickable } from "@warp-ds/react";
import React from "react";
import { Recommendation as RecommendationType } from "src/types.js";
import { useExternalProps } from "../providers/ExternalPropsProvider.js";
import { useHeart } from "../providers/HeartProvider.js";

const gradient =
	"linear-gradient(0deg, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.77) 15.625%, rgba(95, 95, 95, 0.49) 34.895%, rgba(196, 196, 196, 0.00) 50%, rgba(196, 196, 196, 0.00) 100%)";

export const Recommendation = ({ recommendation }: { recommendation: RecommendationType }) => {
	const {
		locale,
		parameters: { variant },
	} = useExternalProps();
	const { Heart } = useHeart();
	const isTouchDevice = window?.matchMedia("(pointer: coarse)").matches;

	return (
		<div
			key={recommendation.itemId}
			data-item-id={recommendation.itemId}
			className={`flex flex-col gap-8 md:gap-16 transition-transform hover:scale-101 focus-within:scale-101 flex-shrink-0 pt-2 ${variant === "infinite-scroll" ? "w-[170] md:w-[304]" : "w-[304]"}`}
		>
			<Card
				className={`rounded-8 border s-border overflow-hidden ${variant === "infinite-scroll" ? "min-h-[120] md:min-h-[224]" : "min-w-[304] min-h-[224]"}`}
			>
				<article className="relative flex items-end p-16 relative h-full s-text-inverted font-bold">
					<div className="z-10">
						<Clickable
							href={`/${recommendation.itemId}`}
							title={recommendation.srText}
							aria-description={recommendation.srText}
							target="__blank"
							className="s-text-inverted font-bold"
							onClick={() => {
								const clickEvent = recommendation.tracking?.pulse?.recommendation?.find(({ type }) => type === "Click");

								if (clickEvent) {
									trackEvent(clickEvent);
								}
							}}
						>
							<span>{recommendation.price}</span>
						</Clickable>
						<Heart
							isMobile={isTouchDevice}
							variant="heart-on-image"
							itemId={Number(recommendation.itemId)}
							itemType="Ad"
							locale={locale}
							buttonClass="absolute top-0 right-0"
						/>
						{recommendation.overlay && (
							<Badge variant="info" position="top-left" className="font-normal">
								{recommendation.overlay.label}
							</Badge>
						)}
					</div>
					<div className="absolute inset-0">
						<div className="absolute inset-0" style={{ background: gradient, zIndex: "1" }} />
						{recommendation.imageUrl ? (
							<>
								<img src={recommendation.imageUrl} className="h-full w-full object-cover absolute inset-0" />
								<img
									src={recommendation.imageUrl}
									className="w-full h-full object-contain backdrop-blur-xxl absolute inset-0 rounded-8"
								/>
							</>
						) : (
							<IconLandscape48 className="h-full w-full s-bg-subtle" />
						)}
					</div>
				</article>
			</Card>
			<div className="flex flex-col gap-8">
				<label className="font-bold truncate">{recommendation.title}</label>
				<span className="text-caption s-text-subtle">{recommendation.metadata}</span>
			</div>
		</div>
	);
};
